/**
 * GTM event utilities.
 *
 * Push custom events to Google Tag Manager's dataLayer.
 * Safe to call server-side (no-op if window unavailable).
 *
 * Usage:
 *   pushGtmEvent({ event: 'purchase', transactionId: 'tx-123', value: 50 });
 *   pushGtmEvent({ event: 'listing_view', listingId: 'l-abc', category: 'rentals' });
 */

/**
 * Push any event to GTM dataLayer.
 * @param {Object} eventData — must include `event` key
 */
export const pushGtmEvent = (eventData) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(eventData);
};

/**
 * Track a page view (useful with client-side routing).
 * @param {string} path — current URL path
 * @param {string} [title] — page title
 */
export const trackPageView = (path, title) => {
  pushGtmEvent({
    event: 'page_view',
    page_path: path,
    page_title: title || document.title,
  });
};

/**
 * Track a listing view.
 * @param {string} listingId
 * @param {string} [listingTitle]
 * @param {string} [category]
 */
export const trackListingView = (listingId, listingTitle, category) => {
  pushGtmEvent({
    event: 'listing_view',
    listing_id: listingId,
    listing_title: listingTitle,
    listing_category: category,
  });
};

/**
 * Track a transaction (purchase/booking).
 * @param {string} transactionId
 * @param {number} value — amount in major currency units (e.g. 50.00)
 * @param {string} currency — ISO 4217 code (e.g. 'EUR')
 */
export const trackTransaction = (transactionId, value, currency) => {
  pushGtmEvent({
    event: 'purchase',
    transaction_id: transactionId,
    value,
    currency,
  });
};

/**
 * Track user login.
 * @param {string} method — 'email' | 'facebook' | 'google'
 */
export const trackLogin = (method) => {
  pushGtmEvent({ event: 'login', method });
};

/**
 * Track user signup.
 * @param {string} method
 */
export const trackSignup = (method) => {
  pushGtmEvent({ event: 'sign_up', method });
};
